/**
 * Derives household activity feed events from existing swipes and feedback.
 * No dedicated activity_events table — events are computed client-side from source data.
 */

import { findHouseholdMatches, formatMatchMessage } from './matchEngine';

const FEED_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export function formatActivityTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function firstName(name) {
  return (name || '').split(' ')[0] || 'Someone';
}

function recipeLabel(recipe) {
  if (!recipe) return 'a recipe';
  const emoji = recipe.emoji ? `${recipe.emoji} ` : '';
  return `${emoji}${recipe.name}`;
}

function starsLabel(stars) {
  if (stars >= 5) return '⭐⭐⭐⭐⭐';
  if (stars >= 4) return `${stars} stars`;
  return `rated ${stars} stars`;
}

/**
 * @returns {Array<{
 *   id: string,
 *   type: 'swipe_like' | 'swipe_pass' | 'rating' | 'match',
 *   memberId: string | null,
 *   memberName: string,
 *   avatar: string,
 *   message: string,
 *   timestamp: string,
 *   recipeId?: string,
 * }>}
 */
export function buildActivityFeed({
  members = [],
  recipes = [],
  rawSwipes = [],
  feedback = [],
  maxAgeMs = FEED_MAX_AGE_MS,
  limit = 50,
}) {
  if (!members.length) return [];

  const memberMap = Object.fromEntries(members.map(m => [m.id, m]));
  const recipeMap = Object.fromEntries(recipes.map(r => [r.id, r]));
  const cutoff = Date.now() - maxAgeMs;
  const events = [];

  const isRecent = (ts) => ts && new Date(ts).getTime() >= cutoff;

  // Individual swipes
  for (const s of rawSwipes) {
    if (!isRecent(s.swiped_at)) continue;
    const member = memberMap[s.member_id];
    if (!member) continue;
    const recipe = recipeMap[s.recipe_id];
    const name = firstName(member.name);
    const label = recipeLabel(recipe);

    events.push({
      id: `swipe-${s.member_id}-${s.recipe_id}`,
      type: s.liked ? 'swipe_like' : 'swipe_pass',
      memberId: s.member_id,
      memberName: name,
      avatar: member.avatar || member.name[0],
      message: s.liked ? `${name} liked ${label}` : `${name} passed on ${label}`,
      timestamp: s.swiped_at,
      recipeId: s.recipe_id,
    });
  }

  // Post-meal ratings
  for (const f of feedback) {
    if (!isRecent(f.feedback_at)) continue;
    const member = memberMap[f.member_id];
    if (!member) continue;
    const recipe = recipeMap[f.recipe_id];
    const name = firstName(member.name);
    const label = recipeLabel(recipe);

    events.push({
      id: `rating-${f.member_id}-${f.recipe_id}-${f.feedback_at}`,
      type: 'rating',
      memberId: f.member_id,
      memberName: name,
      avatar: member.avatar || member.name[0],
      message: `${name} rated ${label} ${starsLabel(f.stars)}`,
      timestamp: f.feedback_at,
      recipeId: f.recipe_id,
    });
  }

  // Match moments — roster consensus (kid/adult aware via matchEngine)
  for (const match of findHouseholdMatches({ members, recipes, rawSwipes, maxAgeMs })) {
    if (!isRecent(match.timestamp)) continue;
    events.push({
      id: `match-${match.recipe.id}`,
      type: 'match',
      memberId: null,
      memberName: 'Your family',
      avatar: '⚡',
      message: formatMatchMessage(match.recipe),
      timestamp: match.timestamp,
      recipeId: match.recipe.id,
    });
  }

  events.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  return events.slice(0, limit);
}
